import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download } from 'lucide-react';

// Simple inline type for install prompt event (not in standard DOM types)
interface InstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'amerikooler_pwa_dismissed';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<InstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const days = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
      if (days < 14) return; // Silenced for 14 days
    }

    const handler = (e: InstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler as unknown as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handler as unknown as EventListener);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto sm:ml-auto sm:mr-4 sm:left-auto sm:right-4 animate-in slide-in-from-bottom-4 duration-300">
      <div className="rounded-2xl border border-border bg-card shadow-lg p-4 flex items-start gap-3">
        <img src="/icon-192.png" alt="Amerikooler" className="h-12 w-12 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Install Amerikooler QS</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add to your home screen for quick access to quotes and pricing.
          </p>
          <div className="flex gap-2 mt-2.5">
            <Button size="sm" className="h-8 gap-1.5 rounded-lg text-xs" onClick={handleInstall}>
              <Download className="h-3.5 w-3.5" />
              Install
            </Button>
            <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs text-muted-foreground" onClick={handleDismiss}>
              Maybe later
            </Button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground shrink-0 -mt-1 -mr-1">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
